import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Trash2, ShoppingBag, Minus, Plus, Tag, Truck } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SALES_TAX_RATE = 0.0825;

const Cart = () => {
  const { cart, removeFromCart, cartId, fetchCart } = useCart();
  const [promoCode, setPromoCode] = useState('');
  const [promoMessage, setPromoMessage] = useState('');
  const [promoError, setPromoError] = useState('');

  useEffect(() => {
    if (cart.promo_code) {
      setPromoCode(cart.promo_code);
    }
  }, [cart.promo_code]);

  const updateQuantity = async (productId, newQuantity) => {
    if (!cartId) return;
    try {
      await axios.put(`${API}/cart/${cartId}/quantity`, {
        product_id: productId,
        quantity: newQuantity
      });
      await fetchCart(cartId);
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  const applyPromoCode = async () => {
    if (!cartId || !promoCode) return;
    setPromoError('');
    setPromoMessage('');
    
    try {
      await axios.post(`${API}/cart/${cartId}/promo`, { code: promoCode });
      setPromoMessage('Promo code applied!');
      await fetchCart(cartId);
      setTimeout(() => setPromoMessage(''), 3000);
    } catch (error) {
      setPromoError(error.response?.data?.detail || 'Invalid promo code');
      setTimeout(() => setPromoError(''), 3000);
    }
  };

  const removePromoCode = async () => {
    if (!cartId) return;
    try {
      await axios.delete(`${API}/cart/${cartId}/promo`);
      setPromoCode('');
      setPromoMessage('Promo code removed');
      await fetchCart(cartId);
      setTimeout(() => setPromoMessage(''), 3000);
    } catch (error) {
      console.error('Error removing promo:', error);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="empty-cart">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-[#8D7B68] mx-auto mb-4" />
          <h2 className="text-3xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Your Cart is Empty
          </h2>
          <p className="text-[#5D4037] mb-8">Add some artisan products to get started.</p>
          <Link to="/" className="btn-primary" data-testid="continue-shopping-button">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  const discountedSubtotal = cart.subtotal - cart.discount;
  const salesTax = Math.round(discountedSubtotal * SALES_TAX_RATE * 100) / 100;
  const grandTotal = Math.round((discountedSubtotal + salesTax) * 100) / 100;

  return (
    <div className="py-20 md:py-32" data-testid="cart-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <h1 className="text-4xl md:text-6xl font-medium mb-12" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="cart-title">
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => (
              <div
                key={`${item.product_id}-${item.variant || 'default'}`}
                className="bg-white/40 border border-[#D7C0A0] p-6 flex flex-col sm:flex-row gap-4"
                data-testid={`cart-item-${item.product_id}`}
              >
                <div className="flex-1">
                  <h3 className="text-xl font-medium mb-1" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="cart-item-name">
                    {item.name}
                  </h3>
                  {item.variant && (
                    <p className="text-sm text-[#8D7B68] mb-1" data-testid="cart-item-variant">Variant: {item.variant}</p>
                  )}
                  <p className="text-[#5D4037] mb-2">${item.price.toFixed(2)} each</p>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex items-center border border-[#D7C0A0] rounded-sm" data-testid="cart-quantity-selector">
                      <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)} className="p-2 hover:bg-[#EBE5DA] transition-colors" data-testid="cart-quantity-decrease">
                        <Minus className="w-4 h-4 text-[#2C1810]" />
                      </button>
                      <span className="w-12 text-center" data-testid="cart-item-quantity">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)} className="p-2 hover:bg-[#EBE5DA] transition-colors" data-testid="cart-quantity-increase">
                        <Plus className="w-4 h-4 text-[#2C1810]" />
                      </button>
                    </div>
                    <button onClick={() => removeFromCart(item.product_id)} className="text-[#5D4037] hover:text-[#B87333] transition-colors flex items-center gap-1" data-testid="remove-item-button">
                      <Trash2 className="w-4 h-4" />
                      <span className="text-sm">Remove</span>
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-medium text-[#B87333]" data-testid="cart-item-total">
                    ${(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-[#EBE5DA] border border-[#D7C0A0] p-6 sticky top-24 overflow-hidden">
              <h2 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Order Summary
              </h2>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-[#5D4037]">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">${cart.subtotal.toFixed(2)}</span>
                </div>
                
                {cart.discount > 0 && (
                  <div className="flex justify-between text-[#B87333] font-medium">
                    <span className="truncate mr-2">Discount ({cart.promo_code})</span>
                    <span className="shrink-0" data-testid="cart-discount">-${cart.discount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-[#5D4037]">
                  <span>Sales Tax (8.25%)</span>
                  <span data-testid="cart-tax">${salesTax.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-[#5D4037]">
                  <span className="flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Shipping</span>
                  <span className="text-green-700 text-sm font-medium" data-testid="cart-shipping">Free – Ships from TX</span>
                </div>
                
                <div className="border-t border-[#D7C0A0] pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>Total</span>
                    <div className="text-right">
                      {cart.discount > 0 && (
                        <p className="text-sm text-[#5D4037] line-through" data-testid="original-total">
                          ${(cart.subtotal + Math.round(cart.subtotal * SALES_TAX_RATE * 100) / 100).toFixed(2)}
                        </p>
                      )}
                      <p className="text-3xl font-medium text-[#B87333]" data-testid="cart-total">
                        ${grandTotal.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <Tag className="w-4 h-4" /> Promo Code
                </label>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase().slice(0, 15))}
                    placeholder="Enter code"
                    maxLength={15}
                    disabled={!!cart.promo_code}
                    className="w-0 flex-1 min-w-0 bg-white border border-[#D7C0A0] px-2 py-2 rounded-sm text-xs focus:border-[#B87333] focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    data-testid="promo-code-input"
                  />
                  {cart.promo_code ? (
                    <button onClick={removePromoCode} className="shrink-0 bg-[#2C1810] text-white px-3 py-2 text-xs rounded-sm hover:bg-[#3D2920] transition-colors" data-testid="remove-promo-button">
                      Remove
                    </button>
                  ) : (
                    <button onClick={applyPromoCode} className="shrink-0 btn-primary px-3 py-2 text-xs" data-testid="apply-promo-button">
                      Apply
                    </button>
                  )}
                </div>
                {promoMessage && <p className="text-xs text-green-600 mt-1" data-testid="promo-success">{promoMessage}</p>}
                {promoError && <p className="text-xs text-red-600 mt-1" data-testid="promo-error">{promoError}</p>}
              </div>

              <Link to="/checkout" className="btn-primary w-full block text-center" data-testid="proceed-to-checkout-button">
                Proceed to Checkout
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
